import React from 'react';
import { theme } from '../config/theme';

/**
 * NeuralMeshCanvas — lightweight 2D canvas network that slowly breathes.
 * - Auto-pauses when out of view
 * - Respects prefers-reduced-motion
 * - Very low opacity, intended as decorative layer under text
 */
export default function NeuralMeshCanvas({ className = '', density = 0.00008 }) {
  const canvasRef = React.useRef(null);
  const frameRef = React.useRef(0);
  const runningRef = React.useRef(true);

  React.useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    let width = canvas.clientWidth;
    let height = canvas.clientHeight;
    const dpr = Math.min(window.devicePixelRatio || 1, 2);
    canvas.width = Math.floor(width * dpr);
    canvas.height = Math.floor(height * dpr);
    ctx.scale(dpr, dpr);

    const prefersReduced = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    const num = Math.max(40, Math.min(220, Math.floor(width * height * density)));

    // Create nodes in an ellipse to suggest a brain-like blob
    const nodes = Array.from({ length: num }).map(() => {
      const angle = Math.random() * Math.PI * 2;
      const r = Math.sqrt(Math.random());
      const rx = (width * 0.35) * r;
      const ry = (height * 0.22) * r;
      const x = width * 0.5 + Math.cos(angle) * rx + (Math.random() - 0.5) * 8;
      const y = height * 0.45 + Math.sin(angle) * ry + (Math.random() - 0.5) * 8;
      return {
        x,
        y,
        vx: (Math.random() - 0.5) * 0.08,
        vy: (Math.random() - 0.5) * 0.08,
      };
    });

    const maxDist = Math.max(60, Math.min(120, Math.hypot(width, height) * 0.06));
    const lineColor = theme.mode === 'light' ? 'rgba(2,6,23,0.06)' : 'rgba(255,255,255,0.08)';
    const dotColor = theme.mode === 'light' ? 'rgba(2,6,23,0.22)' : 'rgba(255,255,255,0.28)';

    let last = 0;
    const draw = (t) => {
      if (!runningRef.current) return;
      frameRef.current = requestAnimationFrame(draw);
      // Throttle to ~30fps
      if (t - last < 33) return;
      last = t;
      ctx.clearRect(0, 0, width, height);

      // Breathing scale
      const s = prefersReduced ? 1 : 1 + Math.sin(t / 4000) * 0.005;

      // Update nodes
      for (const n of nodes) {
        n.x += n.vx;
        n.y += n.vy;
        // Soft bounds toward center
        const cx = width * 0.5, cy = height * 0.45;
        n.vx += ((cx - n.x) * 0.00003);
        n.vy += ((cy - n.y) * 0.00003);
      }

      // Edges
      ctx.lineWidth = 1;
      ctx.strokeStyle = lineColor;
      ctx.beginPath();
      for (let i = 0; i < nodes.length; i++) {
        for (let j = i + 1; j < nodes.length; j++) {
          const a = nodes[i];
          const b = nodes[j];
          const dx = (b.x - width * 0.5) * s + width * 0.5 - ((a.x - width * 0.5) * s + width * 0.5);
          const dy = (b.y - height * 0.5) * s + height * 0.5 - ((a.y - height * 0.5) * s + height * 0.5);
          const d = Math.hypot(dx, dy);
          if (d < maxDist) {
            const alpha = 1 - d / maxDist;
            ctx.globalAlpha = Math.max(0.03, alpha * 0.12);
            ctx.moveTo((a.x - width * 0.5) * s + width * 0.5, (a.y - height * 0.5) * s + height * 0.5);
            ctx.lineTo((b.x - width * 0.5) * s + width * 0.5, (b.y - height * 0.5) * s + height * 0.5);
          }
        }
      }
      ctx.stroke();

      // Dots
      ctx.globalAlpha = 1;
      for (const n of nodes) {
        ctx.fillStyle = dotColor;
        ctx.beginPath();
        ctx.arc((n.x - width * 0.5) * s + width * 0.5, (n.y - height * 0.5) * s + height * 0.5, 1.15, 0, Math.PI * 2);
        ctx.fill();
      }
    };

    frameRef.current = requestAnimationFrame(draw);

    // Pause when not visible
    const io = new IntersectionObserver((entries) => {
      runningRef.current = entries[0]?.isIntersecting ?? true;
      if (runningRef.current && !frameRef.current) frameRef.current = requestAnimationFrame(draw);
      if (!runningRef.current && frameRef.current) {
        cancelAnimationFrame(frameRef.current);
        frameRef.current = 0;
      }
    }, { threshold: 0.05 });
    io.observe(canvas);

    const onResize = () => {
      width = canvas.clientWidth;
      height = canvas.clientHeight;
      canvas.width = Math.floor(width * dpr);
      canvas.height = Math.floor(height * dpr);
      ctx.scale(dpr, dpr);
    };
    const ro = new ResizeObserver(onResize);
    ro.observe(canvas);

    return () => {
      io.disconnect();
      ro.disconnect();
      if (frameRef.current) cancelAnimationFrame(frameRef.current);
    };
  }, [density]);

  return (
    <canvas
      ref={canvasRef}
      className={className}
      style={{ width: '100%', height: '100%', opacity: 0.35 }}
      aria-hidden
    />
  );
}


